import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Input, Select, Card, Space, Tag, Tooltip, Row, Col, Statistic, Typography, Button } from 'antd';
import { SearchOutlined, ShoppingCartOutlined, TruckOutlined, DollarOutlined, CheckCircleOutlined, FilePdfOutlined, CustomerServiceFilled } from '@ant-design/icons';
import NavbarShopy from '../navbarShopy/navbarShopy';
import './shopifyOrdenes.css';

const { Option } = Select;
const { Title } = Typography;

const ShopifyOrdenes = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    entregados: 0,
    enTransito: 0,
    totalUtilidad: 0
  });
  const [filters, setFilters] = useState({
    estadoOrden: '',
    transportadora: '',
    estadoEnvio: '',
  });
  const usuario_id = localStorage.getItem('usuario_id');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`https://api.99envios.app/api/listar_ordenes/${usuario_id}`);
        const filteredData = response.data.filter(item => item.origen_creacion === 6 || item.origen_creacion === 7);
        const formattedData = filteredData.map((item, index) => ({
          key: index,
          id: index + 1,
          estado_orden: item.estado_orden,
          nombre_producto: item.nombre_producto,
          valor_venta: item.valor_venta,
          valor_proveedor: item.valor_proveedor,
          valor_utilidad: item.valor_utilidad,
          transportadora: item.transportadora,
          estado_envio: item.estado_envio,
          fecha_estado: item.fecha_estado,
          numero_guia: item.preeenvio?.numero_preenvio || '',
          valor_flete: item.preeenvio?.valor_flete || '0',
          valor_flete_99: item.preeenvio?.valor_flete_99 || '0',
          valor_declarado: item.preeenvio?.valor_declarado || '0',
          seguro99: item.preeenvio?.seguro99 || 0,
          valor_seguro99: item.preeenvio?.valor_seguro99 || 0,
          destinatario: item.preeenvio?.destinatario || {},
          pdf: item.pdf || '',
        }));
        
        // Ordenar por fecha (más reciente primero)
        const sortedData = [...formattedData].sort((a, b) => {
          return new Date(b.fecha_estado) - new Date(a.fecha_estado);
        });
        
        setData(sortedData);
        setFilteredData(sortedData);
        
        // Calcular estadísticas
        const totalUtilidad = sortedData.reduce((sum, item) => sum + (item.valor_utilidad || 0), 0);
        const entregados = sortedData.filter(item => item.estado_envio?.toLowerCase() === 'entregado').length;
        const enTransito = sortedData.filter(item => item.estado_envio?.toLowerCase() === 'en_transito').length;
        
        setStats({
          total: sortedData.length,
          entregados,
          enTransito,
          totalUtilidad
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [usuario_id]);

  const getEstadoColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'entregado':
        return 'success';
      case 'en_transito':
      case 'en transito':
        return 'processing';
      case 'creado':
        return 'default';
      case 'fallido':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTransportadoraColor = (transportadora) => {
    switch (transportadora?.toLowerCase()) {
      case 'interrapidisimo':
        return '#ff6b35';
      case 'coordinadora':
        return '#1890ff';
      case 'servientrega':
        return '#52c41a';
      case 'envia':
        return '#722ed1';
      default:
        return '#8c8c8c';
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      sorter: (a, b) => Number(a.id) - Number(b.id),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Producto',
      dataIndex: 'nombre_producto',
      key: 'nombre_producto',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_estado',
      key: 'fecha_estado',
      width: 120,
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.fecha_estado) - new Date(b.fecha_estado),
      render: (date) => {
        const formattedDate = new Date(date).toLocaleDateString('es-CO', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        return <span style={{ color: '#666' }}>{formattedDate}</span>;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado_envio',
      key: 'estado_envio',
      width: 120,
      render: (estado) => (
        <Tag color={getEstadoColor(estado)} style={{ fontWeight: 500 }}>
          {estado?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Transportadora',
      dataIndex: 'transportadora',
      key: 'transportadora',
      width: 140,
      render: (transportadora) => (
        <Tag color={getTransportadoraColor(transportadora)} style={{ fontWeight: 500 }}>
          {transportadora?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Guía',
      dataIndex: 'numero_guia',
      key: 'numero_guia',
      width: 130,
      render: (guia) => (
        <span style={{ 
          fontFamily: 'monospace', 
          background: '#f5f5f5', 
          padding: '2px 6px', 
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          {guia || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Valor Proveedor',
      dataIndex: 'valor_proveedor',
      key: 'valor_proveedor',
      width: 130,
      render: (value) => (
        <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
          ${value?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Valor Total',
      dataIndex: 'valor_venta',
      key: 'valor_venta',
      width: 120,
      render: (value) => (
        <span style={{ color: '#1890ff', fontWeight: 600 }}>
          <span style={{ color: '#1890ff' }}>$</span>{value?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Utilidad',
      dataIndex: 'valor_utilidad',
      key: 'valor_utilidad',
      width: 100,
      render: (value) => (
        <span style={{ color: '#52c41a', fontWeight: 600 }}>
          ${value?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Seguro',
      dataIndex: 'seguro99',
      key: 'seguro99',
      width: 80,
      align: 'center',
      render: (value) => value ? 
        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} /> : 
        <span style={{ color: '#d9d9d9' }}>No</span>,
    },
    {
      title: 'Destinatario',
      children: [
        {
          title: 'Nombre',
          dataIndex: ['destinatario', 'nombre'],
          key: 'nombre_destinatario',
          width: 150,
          render: (_, record) => `${record.destinatario.nombre} ${record.destinatario.primer_apellido || ''}`,
        },
        {
          title: 'Teléfono',
          dataIndex: ['destinatario', 'telefono'],
          key: 'telefono_destinatario',
          width: 120,
        },
        {
          title: 'Ciudad',
          dataIndex: ['destinatario', 'ciudad'],
          key: 'ciudad_destinatario',
          width: 150,
          ellipsis:{
            showTitle: false,
          },
          render: (text) => (
            <Tooltip placement='topLeft' title={text}>
              <span>{text}</span>
            </Tooltip>
          ),
        },
        {
          title: 'Dirección',
          dataIndex: ['destinatario', 'direccion'],
          key: 'direccion_destinatario',
          width: 200,
        },
      ],
    },
    {
      title: 'PDF',
      dataIndex: 'pdf',
      key: 'pdf',
      width: 100,
      align: 'center',
      render: (pdf) => pdf ? (
        <Tooltip title="Ver guía PDF">
          <Button 
            type="link" 
            style={{ color: '#1890ff', fontWeight: 500 }}
            onClick={() => window.open(pdf, '_blank')}
          >
            Ver PDF
          </Button>
        </Tooltip>
      ) : (
        <span style={{ color: '#d9d9d9' }}>N/A</span>
      ),
    },
  ];

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters({ ...filters, [filterName]: value });
  };

  useEffect(() => {
    let result = data;
    if (searchText) {
      result = result.filter(
        (item) =>
          item.nombre_producto.toLowerCase().includes(searchText.toLowerCase()) ||
          item.numero_guia.toLowerCase().includes(searchText.toLowerCase()) ||
          (item.destinatario?.nombre && item.destinatario.nombre.toLowerCase().includes(searchText.toLowerCase())) ||
          (item.destinatario?.telefono && item.destinatario.telefono.includes(searchText))
      );
    }
    if (filters.estadoOrden) {
      result = result.filter((item) => item.estado_orden === parseInt(filters.estadoOrden));
    }
    if (filters.transportadora) {
      result = result.filter((item) => item.transportadora.toLowerCase() === filters.transportadora.toLowerCase());
    }
    if (filters.estadoEnvio) {
      result = result.filter((item) => item.estado_envio.toLowerCase() === filters.estadoEnvio.toLowerCase());
    }
    setFilteredData(result);
  }, [searchText, filters, data]);

  return (
    <>
      <NavbarShopy title="Órdenes Shopify" />
      <div className='shopify-misPedidos'>
        {/* Estadísticas */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Órdenes"
                value={stats.total}
                prefix={<ShoppingCartOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Entregados"
                value={stats.entregados}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="En Tránsito"
                value={stats.enTransito}
                prefix={<TruckOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Utilidad Total"
                value={stats.totalUtilidad}
                prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
                formatter={(value) => `$${value?.toLocaleString()}`}
              />
            </Card>
          </Col>
        </Row>

        {/* Filtros y búsqueda */}
        <Card className="filters-card" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Buscar por producto, número de guía, destinatario..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                style={{ width: '100%' }}
                placeholder="Transportadora"
                onChange={(value) => handleFilterChange('transportadora', value)}
                allowClear
              >
                <Option value="">Todas</Option>
                <Option value="interrapidisimo">Interrapidísimo</Option>
                <Option value="coordinadora">Coordinadora</Option>
                <Option value="servientrega">Servientrega</Option>
                <Option value="envia">Envía</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                style={{ width: '100%' }}
                placeholder="Estado de Envío"
                onChange={(value) => handleFilterChange('estadoEnvio', value)}
                allowClear
              >
                <Option value="">Todos</Option>
                <Option value="creado">Creado</Option>
                <Option value="en_transito">En tránsito</Option>
                <Option value="entregado">Entregado</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space>
                <span style={{ color: '#666', fontSize: '14px' }}>
                  Mostrando {filteredData.length} de {data.length} órdenes
                </span>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Tabla */}
        <Card className="table-card">
          <Table
            columns={columns}
            dataSource={filteredData}
            loading={loading}
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} órdenes`,
              pageSizeOptions: ['10', '15', '25', '50'],
            }}
            scroll={{ x: 1500 }}
            locale={{ emptyText: 'No hay órdenes disponibles' }}
            size="middle"
          />
        </Card>
      </div>
    </>
  );
};

export default ShopifyOrdenes;
